import { ColumnsType } from 'antd/es/table';
import { Table } from 'shared/ui/Table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableActions, ServerTableRowSelectionActions } from 'shared/ui/ServerTable/types';
import { useTableFilterContext } from 'features/TableFilter';
import { Box, useGetUnitsQuery } from 'entities/Box';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { useGetWarehouseByIdQuery } from 'entities/Warehouse';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { stepperModalActions } from 'features/StepperModal';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { transformZoneBoxesListFilters } from '../../utils/transformZoneBoxesListFilters';
import { getZoneBasics } from '../../model/selectors';
import { createZoneModalActions } from '../../model/slice/createZoneModalSlice';
import { Button } from 'shared/ui/Button';
import { Door } from '../../model/types';
import { BoxesListFilter } from './BoxesListFilter';

export const SelectObjectsStep: FC = memo(() => {
  const { t } = useAppTranslation(['boxes', 'common']);
  const dispatch = useAppDispatch();
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const basics = useAppSelector(getZoneBasics);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();
  const [view, setView] = useState<'boxes' | 'doors'>('boxes');
  const [selectedDoorKeys, setSelectedDoorKeys] = useState<React.Key[]>([]);

  const { data: warehouse } = useGetWarehouseByIdQuery(
    { warehouseId, params: { floors: true, internalEquipment: true } },
    { skip: !warehouseId },
  );

  const tableActions: Array<ServerTableActions<Box>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'boxes' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t],
  );

  const boxColumns = useMemo<ColumnsType<Box>>(
    () => [
      { title: t('Unit', { ns: 'boxes' }), key: 'name', render: (_, record) => record.name },
      {
        title: t('Size', { ns: 'boxes' }),
        key: 'size',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => `${formatDecimal(record.sizeCode.square)} ${getSizeCodeTypeString(record.sizeCode.sizeCodeType)}`,
      },
      {
        title: t('Location', { ns: 'boxes' }),
        key: 'floor',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => `${getLocalizedString(record.floor?.name) || ''}`,
      },
    ],
    [t],
  );

  const boxRowSelectionActions: Array<ServerTableRowSelectionActions<Box>> = useMemo(
    () => [
      {
        name: t('Next', { ns: 'common' }),
        onClick: (selected: Box[]) => {
          const ids = selected.map((box) => box.boxId);

          dispatch(createZoneModalActions.setSelectedBoxIds(ids));
          setView('doors');
        },
      },
    ],
    [dispatch, t],
  );

  const doorColumns = useMemo<ColumnsType<Door>>(
    () => [{ title: t('Door name', { ns: 'boxes' }), key: 'name', render: (_, record) => getLocalizedString(record.name) }],
    [t],
  );

  const doors: Door[] = useMemo(() => {
    const warehouseDoors = warehouse?.internalEquipments || [];

    return warehouseDoors.map((door) => ({
      internalEquipmentId: door.internalEquipmentId,
      name: door.name,
      lockAddress: door.lockAddress || null,
    }));
  }, [warehouse]);

  const onDoorSelectChange = useCallback((keys: React.Key[]) => {
    setSelectedDoorKeys(keys);
  }, []);

  const skipDoors = useCallback(() => {
    dispatch(createZoneModalActions.setSelectedDoorIds([]));
    dispatch(stepperModalActions.changeActiveStepIndex(2));
  }, [dispatch]);

  const backToBoxes = useCallback(() => {
    setView('boxes');
  }, []);

  const skipBoxes = useCallback(() => {
    setView('doors');
  }, []);

  return (
    <>
      <div className={view === 'boxes' ? 'block' : 'hidden'}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-2xl font-semibold">{t('Choose units', { ns: 'warehouses' })}</div>
          <div className="flex gap-2">
            <Button
              theme="secondary"
              onClick={() => {
                dispatch(stepperModalActions.changeActiveStepIndex(0));
              }}
            >
              {t('Back', { ns: 'common' })}
            </Button>
            <Button theme="secondary" onClick={skipBoxes}>
              {t('Skip', { ns: 'common' })}
            </Button>
          </div>
        </div>
        <ServerTable
          columns={boxColumns}
          rowKey="boxId"
          search
          selectionType="checkbox"
          rowSelectionActions={boxRowSelectionActions}
          fetchData={useGetUnitsQuery}
          onAllFiltersClear={clearAllFilters}
          defaultFilters={{
            warehouseId: warehouseId || basics?.warehouse?.warehouseId,
            ...transformZoneBoxesListFilters(appliedFilters),
          }}
          tags={tags}
          tableActions={tableActions}
          tableActionsPosition="post"
        />
        <BoxesListFilter />
      </div>

      <div className={view === 'doors' ? 'block' : 'hidden'}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-2xl font-semibold">{t('Choose units', { ns: 'warehouses' })}</div>
          <div className="flex gap-2">
            <Button theme="secondary" onClick={backToBoxes}>
              {t('Back', { ns: 'common' })}
            </Button>
            <Button theme="secondary" onClick={skipDoors}>
              {t('Skip', { ns: 'common' })}
            </Button>
          </div>
        </div>

        <Table
          rowKey="internalEquipmentId"
          columns={doorColumns}
          data={doors}
          pagination={false}
          selectionType="checkbox"
          rowSelectionEnabled
          selectedRowKeys={selectedDoorKeys}
          onSelectionChange={(keys) => {
            onDoorSelectChange(keys);
          }}
        />
      </div>
    </>
  );
});
